import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useRoutingForms } from "./context/RoutingFormsContext";
import { useMutation } from "@apollo/client";
import { UPDATE_ROUTING_CONFIG } from "./graphql/operations";
import {
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
  Alert,
  ButtonGroup,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterAlt as FilterIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";

// Styled components
const Card = styled(MuiCard)(spacing);

const RouteCard = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  transition: "box-shadow 0.2s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[2],
  },
  position: "relative",
  overflow: "hidden",
}));

const RouteCardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: theme.palette.background.default,
}));

const RouteCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const RouteActions = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  display: "flex",
  justifyContent: "flex-end",
}));

const RuleContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

const OperatorSelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px dashed ${theme.palette.divider}`,
}));

const FallbackCard = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(3),
}));

const FallbackHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const FallbackContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0, 0.5),
}));

// Define destination types with labels and descriptions
const destinationTypes = [
  {
    value: "CUSTOM_PAGE",
    label: "Custom Page",
    description: "Show a custom message to the form submitter",
  },
  {
    value: "EXTERNAL_REDIRECT",
    label: "External Redirect",
    description: "Redirect to an external website",
  },
  {
    value: "EVENT_REDIRECT",
    label: "Event Redirect",
    description: "Redirect to one of your event booking pages",
  },
];

// Define operator types
const operators = [
  { value: "EQUALS", label: "Equals" },
  { value: "NOT_EQUALS", label: "Does Not Equal" },
  { value: "CONTAINS", label: "Contains" },
  { value: "NOT_CONTAINS", label: "Does Not Contain" },
  { value: "GREATER_THAN", label: "Greater Than" },
  { value: "LESS_THAN", label: "Less Than" },
  { value: "IS_EMPTY", label: "Is Empty" },
  { value: "IS_NOT_EMPTY", label: "Is Not Empty" },
];

// Define logical operators for combining rules
const logicalOperators = [
  {
    value: "AND",
    label: "All",
    description: "All conditions must match (AND)",
  },
  {
    value: "OR",
    label: "Any",
    description: "At least one condition must match (OR)",
  },
  {
    value: "NOT",
    label: "None",
    description: "No conditions should match (NOT)",
  },
];

// Single rule component
function Rule({ rule, fields, onUpdate, onDelete }) {
  const handleFieldChange = (e) => {
    onUpdate({ ...rule, field: e.target.value });
  };

  const handleOperatorChange = (e) => {
    onUpdate({ ...rule, operator: e.target.value });
  };

  const handleValueChange = (e) => {
    onUpdate({ ...rule, value: e.target.value });
  };

  // Check if operator needs a value input
  const noValueNeeded = ["IS_EMPTY", "IS_NOT_EMPTY"].includes(rule.operator);

  // Get the field data for this rule
  const fieldData = fields.find((f) => f.id === rule.field);

  return (
    <RuleContainer>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Field</InputLabel>
            <Select
              value={rule.field || ""}
              onChange={handleFieldChange}
              label="Field"
            >
              <MenuItem disabled value="">
                <em>Select a field</em>
              </MenuItem>
              {fields.map((field) => (
                <MenuItem key={field.id} value={field.id}>
                  {field.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Operator</InputLabel>
            <Select
              value={rule.operator || ""}
              onChange={handleOperatorChange}
              label="Operator"
              disabled={!rule.field}
            >
              <MenuItem disabled value="">
                <em>Select an operator</em>
              </MenuItem>
              {operators.map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4} md={5}>
          {!noValueNeeded ? (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Value"
              placeholder={
                fieldData ? `Enter ${fieldData.label} value` : "Value"
              }
              value={rule.value || ""}
              onChange={handleValueChange}
              disabled={!rule.operator || noValueNeeded}
            />
          ) : (
            <Box pl={1} py={1}>
              <Typography variant="body2" color="textSecondary">
                No value required for this operator
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={1} textAlign="right">
          <Tooltip title="Remove Rule">
            <IconButton onClick={onDelete} size="small" color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </RuleContainer>
  );
}

// Destination component - renders destination selection and configuration
function Destination({ destination, eventTypes, onUpdate }) {
  const handleTypeChange = (e) => {
    onUpdate({
      type: e.target.value,
      value: "",
    });
  };

  const handleValueChange = (e) => {
    onUpdate({
      ...destination,
      value: e.target.value,
    });
  };

  const handleCustomEventChange = (e) => {
    onUpdate({
      ...destination,
      customEventValue: e.target.value,
    });
  };

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth variant="outlined">
          <InputLabel>Destination Type</InputLabel>
          <Select
            value={destination.type || "CUSTOM_PAGE"}
            onChange={handleTypeChange}
            label="Destination Type"
          >
            {destinationTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {destinationTypes.find((t) => t.value === destination.type)
              ?.description || "Where to send the form submitter"}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={8}>
        {destination.type === "CUSTOM_PAGE" && (
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Custom Message"
            placeholder="Enter the message to display to the user"
            value={destination.value || ""}
            onChange={handleValueChange}
          />
        )}

        {destination.type === "EXTERNAL_REDIRECT" && (
          <TextField
            fullWidth
            variant="outlined"
            label="Redirect URL"
            placeholder="https://example.com/page"
            value={destination.value || ""}
            onChange={handleValueChange}
            InputProps={{
              startAdornment: destination.value ? null : (
                <InputAdornment position="start">https://</InputAdornment>
              ),
            }}
            helperText="Enter the full URL where the user should be redirected"
          />
        )}

        {destination.type === "EVENT_REDIRECT" && (
          <>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={destination.value || ""}
                onChange={handleValueChange}
                label="Event Type"
              >
                <MenuItem value="">
                  <em>Select an event type</em>
                </MenuItem>
                <MenuItem value="custom">Custom URL</MenuItem>
                {eventTypes.map((event) => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select an event type or enter a custom URL
              </FormHelperText>
            </FormControl>

            {destination.value === "custom" && (
              <TextField
                fullWidth
                variant="outlined"
                label="Custom Event URL"
                placeholder="my-team/event-name"
                value={destination.customEventValue || ""}
                onChange={handleCustomEventChange}
                sx={{ mt: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">team/</InputAdornment>
                  ),
                }}
                helperText="You can use field variables like {email} in your URL"
              />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
}

// Route component - combines rules and destination
function RouteComponent({
  route,
  index,
  fields,
  eventTypes,
  onUpdate,
  onDelete,
  onAddRule,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleDestinationUpdate = (destination) => {
    onUpdate({
      ...route,
      destination,
    });
  };

  const handleLogicalOperatorChange = (e) => {
    onUpdate({
      ...route,
      logicalOperator: e.target.value,
    });
  };

  const handleRuleUpdate = (index, updatedRule) => {
    const newRules = [...route.rules];
    newRules[index] = updatedRule;
    onUpdate({
      ...route,
      rules: newRules,
    });
  };

  const handleRuleDelete = (index) => {
    const newRules = [...route.rules];
    newRules.splice(index, 1);
    onUpdate({
      ...route,
      rules: newRules,
    });
  };

  return (
    <RouteCard>
      <RouteCardHeader>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" sx={{ mr: 2 }}>
            Route {index + 1}
          </Typography>

          <IconButton size="small" onClick={toggleExpanded} color="primary">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Box>
          <ButtonGroup size="small">
            <Tooltip title="Move Up">
              <span>
                <ActionButton
                  disabled={isFirst}
                  onClick={onMoveUp}
                  size="small"
                >
                  ↑
                </ActionButton>
              </span>
            </Tooltip>
            <Tooltip title="Move Down">
              <span>
                <ActionButton
                  disabled={isLast}
                  onClick={onMoveDown}
                  size="small"
                >
                  ↓
                </ActionButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Route">
              <ActionButton
                onClick={() => onDelete(route.id)}
                color="error"
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          </ButtonGroup>
        </Box>
      </RouteCardHeader>

      <RouteCardContent>
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Destination
          </Typography>
          <Box display="flex" alignItems="center" mb={1}>
            <ArrowForwardIcon color="action" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2" color="textSecondary">
              Where to send the form submitter if this route matches
            </Typography>
          </Box>
          <Destination
            destination={route.destination}
            eventTypes={eventTypes}
            onUpdate={handleDestinationUpdate}
          />
        </Box>

        {expanded && (
          <>
            <Divider sx={{ my: 3 }} />

            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                Conditions
              </Typography>

              <Box display="flex" alignItems="center" mb={1}>
                <FilterIcon color="action" fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Define when this route should be used
                </Typography>
              </Box>

              {route.rules.length > 0 && (
                <OperatorSelector>
                  <Typography variant="body2" sx={{ mr: 2 }}>
                    Match
                  </Typography>
                  <FormControl component="fieldset" size="small">
                    <RadioGroup
                      row
                      value={route.logicalOperator || "AND"}
                      onChange={handleLogicalOperatorChange}
                    >
                      {logicalOperators.map((op) => (
                        <FormControlLabel
                          key={op.value}
                          value={op.value}
                          control={<Radio size="small" />}
                          label={op.label}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <Tooltip title="Determines how multiple conditions are combined">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </OperatorSelector>
              )}

              <Box>
                {route.rules.map((rule, index) => (
                  <Rule
                    key={index}
                    rule={rule}
                    fields={fields}
                    onUpdate={(updatedRule) =>
                      handleRuleUpdate(index, updatedRule)
                    }
                    onDelete={() => handleRuleDelete(index)}
                  />
                ))}

                {route.rules.length === 0 && (
                  <Alert
                    severity="info"
                    action={
                      <Button color="inherit" size="small" onClick={onAddRule}>
                        Add Rule
                      </Button>
                    }
                  >
                    No rules defined. This route will match all submissions
                    unless you add conditions.
                  </Alert>
                )}
              </Box>

              <RouteActions>
                <Button
                  startIcon={<AddIcon />}
                  onClick={onAddRule}
                  variant={route.rules.length === 0 ? "contained" : "outlined"}
                  size="small"
                >
                  Add Rule
                </Button>
              </RouteActions>
            </Box>
          </>
        )}
      </RouteCardContent>
    </RouteCard>
  );
}

function RoutingTab({ form, setForm }) {
  // Try to use the routing forms context, but don't require it
  let contextEventTypes, updateFormContext;
  let contextAvailable = false;

  try {
    // Try to use the context, but catch the error if it's not available
    const routingContext = useRoutingForms();
    contextEventTypes = routingContext.eventTypes;
    updateFormContext = routingContext.updateForm;
    contextAvailable = true;
  } catch (error) {
    // Context not available, we'll use fallbacks
    console.log("RoutingForms context not available, using fallbacks");
  }

  // Initialize event types from context or use hardcoded ones as fallback
  const eventTypes =
    contextAvailable && contextEventTypes && contextEventTypes.length > 0
      ? contextEventTypes
      : [
          { id: "evt1", title: "30 Minute Meeting" },
          { id: "evt2", title: "15 Minute Intro Call" },
          { id: "evt3", title: "Product Demo" },
          { id: "evt4", title: "Team Meeting" },
        ];

  // Setup GraphQL mutation for updating routing config
  const [updateRoutingConfig, { loading: mutationLoading }] = useMutation(
    UPDATE_ROUTING_CONFIG
  );

  // State for tracking loading status
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [fallback, setFallback] = useState({
    type: "CUSTOM_PAGE",
    value: "Thank you for your submission!",
  });
  const [fields, setFields] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Initialize state from form data
  useEffect(() => {
    console.log("RoutingTab: form prop changed:", form);
    console.log("RoutingTab: form.routes =", form?.routes);
    console.log("RoutingTab: form.fallback =", form?.fallback);
    console.log("RoutingTab: form.fields =", form?.fields);

    if (form) {
      // Extract routes from form data if available
      if (form.routes && Array.isArray(form.routes)) {
        console.log("RoutingTab: Setting routes from form.routes");
        setRoutes(form.routes);
      }

      // Extract fallback from form data if available
      if (form.fallback) {
        console.log("RoutingTab: Setting fallback from form.fallback");
        setFallback(form.fallback);
      }

      // Extract fields from form data if available
      if (form.fields && Array.isArray(form.fields)) {
        console.log("RoutingTab: Setting fields from form.fields");
        setFields(form.fields);
      } else {
        // Set default sample fields if none exist
        console.log("RoutingTab: No fields in form, using sample fields");
        const sampleFields = [
          { id: "firstName", label: "First Name", type: "text" },
          { id: "lastName", label: "Last Name", type: "text" },
          { id: "email", label: "Email Address", type: "email" },
          { id: "phone", label: "Phone Number", type: "phone" },
          { id: "company", label: "Company", type: "text" },
          { id: "jobTitle", label: "Job Title", type: "text" },
          { id: "interestLevel", label: "Interest Level", type: "select" },
          { id: "source", label: "How did you hear about us?", type: "select" },
        ];
        setFields(sampleFields);
      }
    }
  }, [form]);

  // Add a new route
  const handleAddRoute = () => {
    const newRoute = {
      id: `r${Date.now()}`,
      destination: {
        type: "CUSTOM_PAGE",
        value: "",
      },
      logicalOperator: "AND",
      rules: [],
    };

    setRoutes([...routes, newRoute]);
  };

  // Update a route
  const handleUpdateRoute = (routeId, updatedRoute) => {
    setRoutes(
      routes.map((route) =>
        route.id === routeId ? { ...updatedRoute, id: routeId } : route
      )
    );
  };

  // Delete a route
  const handleDeleteRoute = (routeId) => {
    setRoutes(routes.filter((route) => route.id !== routeId));
  };

  // Move a route up in the list
  const handleMoveRouteUp = (index) => {
    if (index === 0) return;

    const newRoutes = [...routes];
    const temp = newRoutes[index];
    newRoutes[index] = newRoutes[index - 1];
    newRoutes[index - 1] = temp;

    setRoutes(newRoutes);
  };

  // Move a route down in the list
  const handleMoveRouteDown = (index) => {
    if (index === routes.length - 1) return;

    const newRoutes = [...routes];
    const temp = newRoutes[index];
    newRoutes[index] = newRoutes[index + 1];
    newRoutes[index + 1] = temp;

    setRoutes(newRoutes);
  };

  // Add a rule to a route
  const handleAddRule = (routeId) => {
    const route = routes.find((r) => r.id === routeId);
    if (!route) return;

    const newRule = {
      id: `rule${Date.now()}`,
      field: "",
      operator: "",
      value: "",
    };

    handleUpdateRoute(routeId, {
      ...route,
      rules: [...route.rules, newRule],
    });
  };

  // Update fallback settings
  const handleFallbackTypeChange = (e) => {
    setFallback({
      ...fallback,
      type: e.target.value,
      value: "", // Reset value when type changes
    });
  };

  const handleFallbackValueChange = (e) => {
    setFallback({
      ...fallback,
      value: e.target.value,
    });
  };

  // Save all routing settings
  const handleSave = async () => {
    setLoading(true);

    try {
      // Check for form ID
      const formId = form._id || form.id;

      if (!formId) {
        throw new Error("Form ID is missing");
      }

      // Import axiosInstance for API call
      const { axiosInstance } = await import("../../../utils/axios");

      // Call the backend to update the routing form with routes and fallback
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation UpdateRoutingForm($id: ID!, $input: RoutingFormInput!) {
            updateRoutingForm(id: $id, input: $input) {
              _id
              name
              description
              status
              fields
              routes
              fallback
              emailSettings
              createdAt
              updatedAt
            }
          }
        `,
        variables: {
          id: formId,
          input: {
            name: form.name, // name is required
            routes: routes,
            fallback: fallback,
          },
        },
      });

      console.log("Update routing response:", response.data);

      const result =
        response.data?.data?.updateRoutingForm ||
        response.data?.updateRoutingForm;

      if (result) {
        // Update the parent component's state with the server response
        setForm({
          ...form,
          ...result,
          // Parse JSON fields if they are strings
          fields:
            typeof result.fields === "string"
              ? JSON.parse(result.fields)
              : result.fields || form.fields,
          routes:
            typeof result.routes === "string"
              ? JSON.parse(result.routes)
              : result.routes || routes,
          fallback:
            typeof result.fallback === "string"
              ? JSON.parse(result.fallback)
              : result.fallback || fallback,
          emailSettings:
            typeof result.emailSettings === "string"
              ? JSON.parse(result.emailSettings)
              : result.emailSettings || form.emailSettings,
        });

        setNotification({
          open: true,
          message: "Routing configuration saved successfully",
          severity: "success",
        });
      } else {
        throw new Error("No data returned from server");
      }
    } catch (error) {
      console.error("Error saving routing configuration:", error);
      setNotification({
        open: true,
        message: error.message || "An error occurred while saving",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Routing
      </Typography>
      <Typography variant="body2" paragraph>
        Define rules to route form submissions to different destinations based
        on answers. Changes will be saved to the backend database.
      </Typography>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Main Routes Section */}
      <Box mb={4}>
        {routes.length > 0 ? (
          routes.map((route, index) => (
            <RouteComponent
              key={route.id}
              route={route}
              index={index}
              fields={fields}
              eventTypes={eventTypes}
              onUpdate={(updatedRoute) =>
                handleUpdateRoute(route.id, updatedRoute)
              }
              onDelete={handleDeleteRoute}
              onAddRule={() => handleAddRule(route.id)}
              onMoveUp={() => handleMoveRouteUp(index)}
              onMoveDown={() => handleMoveRouteDown(index)}
              isFirst={index === 0}
              isLast={index === routes.length - 1}
            />
          ))
        ) : (
          <Paper
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: (theme) => theme.palette.background.default,
              mb: 3,
            }}
          >
            <Typography variant="body1" paragraph>
              No routes defined yet. Routes are processed in order, with the
              first matching route being used.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddRoute}
            >
              Add Your First Route
            </Button>
          </Paper>
        )}

        {routes.length > 0 && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddRoute}
            fullWidth
          >
            Add Another Route
          </Button>
        )}
      </Box>

      {/* Fallback Section */}
      <FallbackCard>
        <FallbackHeader>
          <Typography variant="h6">Fallback Route</Typography>
          <Typography variant="body2" color="textSecondary">
            Used when no other routes match
          </Typography>
        </FallbackHeader>

        <FallbackContent>
          <Destination
            destination={fallback}
            eventTypes={eventTypes}
            onUpdate={setFallback}
          />
        </FallbackContent>
      </FallbackCard>

      {/* Save Button */}
      <Box
        mt={4}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="body2" color="textSecondary">
          {form && form._id
            ? `Saving to form ID: ${form._id}`
            : "Form not yet saved - changes will be stored locally"}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          size="large"
          startIcon={<SaveIcon />}
          disabled={loading || mutationLoading}
        >
          {loading || mutationLoading ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              Saving to Backend...
            </>
          ) : (
            "Save Routing Configuration"
          )}
        </Button>
      </Box>
    </Box>
  );
}

export default RoutingTab;
